import { Dispatch, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TRequestStatus } from "@models/msc.model";
import schedulerService from "@/services/scheduler.service";
import { RootState } from "..";
import type {
  SchedulerResponse,
  CreateSchedulerRequest,
  UpdateSchedulerRequest,
} from "@/models/scheduler.model";

type SchedulersState = {
  initialized: boolean;
  status: TRequestStatus;
  schedulers: SchedulerResponse[];
};

const initialState: SchedulersState = {
  initialized: false,
  status: "IDLE",
  schedulers: [],
};

const schedulersSlice = createSlice({
  name: "schedulers",
  initialState,
  reducers: {
    startLoading(state) {
      state.status = "PENDING";
    },
    gotSchedulers(state, action: PayloadAction<SchedulerResponse[]>) {
      state.schedulers = action.payload;
      state.status = "IDLE";
      state.initialized = true;
    },
    schedulerCreated(state, action: PayloadAction<SchedulerResponse>) {
      state.schedulers.unshift(action.payload);
      state.status = "IDLE";
    },
    schedulerUpdated(state, action: PayloadAction<SchedulerResponse>) {
      state.schedulers = state.schedulers.map((s) =>
        s.id === action.payload.id ? action.payload : s
      );
      state.status = "IDLE";
    },
    schedulerRemoved(state, action: PayloadAction<number>) {
      state.schedulers = state.schedulers.filter((s) => s.id !== action.payload);
      state.status = "IDLE";
    },
    resetStatus(state) {
      state.status = "IDLE";
    },
  },
});

// Thunk Actions

export const getAllSchedulers = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const {
    schedulers: { schedulers, initialized },
  } = getState();

  if (initialized) return schedulers;

  dispatch(schedulersSlice.actions.startLoading());
  const response = await schedulerService.getAll();
  dispatch(schedulersSlice.actions.gotSchedulers(response));
  return response;
};

export const createScheduler = (data: CreateSchedulerRequest) => async (dispatch: Dispatch) => {
  dispatch(schedulersSlice.actions.startLoading());
  const response = await schedulerService.create(data);
  dispatch(schedulersSlice.actions.schedulerCreated(response));
  return response;
};

export const updateScheduler = (id: number, data: UpdateSchedulerRequest) => async (dispatch: Dispatch) => {
  dispatch(schedulersSlice.actions.startLoading());
  const response = await schedulerService.updateById(id, data);
  dispatch(schedulersSlice.actions.schedulerUpdated(response));
  return response;
};

export const deleteScheduler = (id: number) => async (dispatch: Dispatch) => {
  dispatch(schedulersSlice.actions.startLoading());
  await schedulerService.deleteById(id);
  dispatch(schedulersSlice.actions.schedulerRemoved(id));
};

// Selectors
export const selectSchedulers = (state: RootState) => state.schedulers.schedulers;
export const selectSchedulersStatus = (state: RootState) => state.schedulers.status;
export const selectSchedulersInitialized = (state: RootState) => state.schedulers.initialized;

export default schedulersSlice.reducer;
